import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatError } from "../lib/api.js";
import {
  AlertCircle,
  CheckCircle2,
  Download,
  FileSpreadsheet,
  ListChecks,
  Trash2,
  Upload,
  X,
} from "lucide-react";

/**
 * "Criar PCP" panel triggered from the SOM detail page when the SOM
 * transitions to "Aguarda classificação". The panel:
 *   • Shows the SOM identity as read-only labels (no re-typing).
 *   • Lets the classifier download the generic Excel template
 *     (`template_pcp_novo.xlsx`) and import a parts list before the PCP
 *     record is persisted. Import is validated server-side (no DB writes)
 *     and reviewed inline; the parts payload is then sent as part of the
 *     `POST /classification-requests` body so the PCP is created with all
 *     lines in a single operation.
 *   • Type / classifier / priority / findings are still editable.
 */
export default function NewClassificationRequestDialog({ case_, onCreated, onClose }) {
  const navigate = useNavigate();
  const [v2enums, setV2enums] = useState(null);
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({
    classification_type: "",
    classifier_id: "",
    priority: case_?.priority || "Normal",
    description: "",
  });

  // Excel-import state (all client-side; only committed when the PCP is saved).
  const fileRef = useRef(null);
  const [importBusy, setImportBusy] = useState(false);
  const [importError, setImportError] = useState("");
  const [validationErrors, setValidationErrors] = useState(null);
  const [preview, setPreview] = useState(null);
  const [parts, setParts] = useState([]); // committed parts staged for save

  useEffect(() => {
    api.get("/v2/enums").then((r) => setV2enums(r.data));
    api.get("/users").then((r) => setUsers(r.data.filter((u) => !u.deactivated)));
  }, []);

  useEffect(() => {
    if (!v2enums) return;
    setForm((f) =>
      f.classification_type && v2enums.classification_types.includes(f.classification_type)
        ? f
        : { ...f, classification_type: v2enums.classification_types[0] }
    );
  }, [v2enums]);

  const classifiers = useMemo(
    () =>
      users.filter((u) =>
        ["workshop_coordinator", "classifier", "workshop_supervisor", "department_manager"].includes(u.role)
      ),
    [users]
  );

  useEffect(() => {
    if (!form.classifier_id && classifiers.length && case_) {
      const branchMatch = classifiers.find(
        (u) => u.role === "workshop_coordinator" && u.branch === case_.branch
      );
      setForm((f) => ({ ...f, classifier_id: (branchMatch || classifiers[0]).id }));
    }
  }, [classifiers, case_, form.classifier_id]);

  const canSubmit = form.description.trim().length > 0 && form.classification_type;

  // ----- Excel handlers -----
  const downloadTemplate = async () => {
    setImportError("");
    try {
      const res = await api.get(`/classification-requests/parts-template-blank`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "template_pcp_novo.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setImportError(formatError(e?.response?.data?.detail) || "Falha ao descarregar o template.");
    }
  };

  const onPickFile = () => {
    setImportError("");
    setValidationErrors(null);
    setPreview(null);
    fileRef.current?.click();
  };

  const onFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const lower = file.name.toLowerCase();
    if (!lower.endsWith(".xlsx") && !lower.endsWith(".xls")) {
      setImportError("Apenas ficheiros .xlsx ou .xls são aceites.");
      return;
    }
    setImportBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/classification-requests/parts-import-validate`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data.status === "errors") setValidationErrors(data);
      else if (data.status === "preview") setPreview(data);
    } catch (er) {
      setImportError(formatError(er?.response?.data?.detail) || "Falha ao processar o ficheiro.");
    } finally {
      setImportBusy(false);
    }
  };

  const confirmImport = () => {
    if (!preview) return;
    const rows = preview.rows.map(({ row, ...rest }) => rest);
    setParts((existing) => [...existing, ...rows]);
    setPreview(null);
  };

  const removePart = (idx) => {
    setParts((p) => p.filter((_, i) => i !== idx));
  };

  const submit = async () => {
    setErr("");
    if (!canSubmit) {
      setErr("Preencha os apuramentos técnicos antes de submeter.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        service_case_id: case_.id,
        classification_type: form.classification_type,
        description: form.description.trim(),
        classifier_id: form.classifier_id || null,
        priority: form.priority,
      };
      if (parts.length) {
        payload.classified_parts = parts.map((p) => ({
          part_number: p.part_number,
          description: p.description,
          quantity: p.quantity,
          brand: p.brand,
          source: p.source,
          notes: p.notes || "",
        }));
      }
      const { data } = await api.post("/classification-requests", payload);
      if (onCreated) onCreated(data);
      else navigate(`/classification-requests/${data.id}`);
    } catch (e) {
      setErr(formatError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!case_) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/40"
      onClick={onClose}
      data-testid="new-pcp-dialog"
    >
      <div
        className="bg-white w-full max-w-3xl h-full overflow-y-auto shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white px-5 py-3 border-b border-gray-200 flex items-center justify-between z-10">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#6C757D]">
              Nova Classificação de Peças
            </div>
            <h2 className="font-heading text-base font-bold text-[#1A1A2E]">
              Criar PCP para {case_.number}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-[#6C757D] hover:text-[#1A1A2E]"
            aria-label="Fechar"
            data-testid="new-pcp-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Read-only prefilled block */}
          <div className="rounded-sm border border-gray-200 bg-[#F8FAFC] p-3 grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
            <ReadRow label="SOM" value={case_.number} testid="new-pcp-som" mono />
            <ReadRow
              label="Job card Alidata"
              value={case_.alidata_job_card}
              testid="new-pcp-alidata"
              mono
            />
            <ReadRow
              label="Cliente"
              value={case_.customer_name}
              testid="new-pcp-customer"
            />
            <ReadRow
              label="Viatura"
              value={`${case_.brand || ""} ${case_.model || ""} · ${case_.vehicle_registration || ""}`.trim()}
              testid="new-pcp-vehicle"
            />
            <ReadRow label="VIN" value={case_.vin} testid="new-pcp-vin" mono />
            <ReadRow label="Sucursal" value={case_.branch} testid="new-pcp-branch" />
            <ReadRow
              label="Service Advisor"
              value={case_.service_advisor?.name || "—"}
              testid="new-pcp-advisor"
            />
          </div>

          <Field label="Tipo de classificação *">
            <select
              value={form.classification_type}
              onChange={(e) => setForm({ ...form, classification_type: e.target.value })}
              className={inputCls}
              data-testid="new-pcp-type"
            >
              {(v2enums?.classification_types || []).map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Apuramentos técnicos *">
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={4}
              className={inputCls}
              placeholder="Descreve o diagnóstico técnico, sintomas observados e causa identificada..."
              data-testid="new-pcp-description"
            />
          </Field>

          <Field label="Classificador responsável">
            <select
              value={form.classifier_id}
              onChange={(e) => setForm({ ...form, classifier_id: e.target.value })}
              className={inputCls}
              data-testid="new-pcp-classifier"
            >
              <option value="">(sem atribuição)</option>
              {classifiers.map((u) => (
                <option key={u.id} value={u.id}>
                  {`${u.name} · ${u.role.replaceAll("_", " ")}${u.branch ? ` · ${u.branch}` : ""}`}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Prioridade">
            <select
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: e.target.value })}
              className={inputCls}
              data-testid="new-pcp-priority"
            >
              {["Low", "Normal", "High", "Critical"].map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </Field>

          {/* === Peças classificadas (import ANTES de gravar) === */}
          <div className="border-t border-gray-100 pt-3">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <h3 className="font-heading text-sm font-bold text-[#1A1A2E] flex items-center gap-1.5">
                <ListChecks className="w-4 h-4 text-[#1F3864]" />
                Peças classificadas
                <span
                  className="text-xs font-normal text-[#6C757D]"
                  data-testid="new-pcp-parts-count"
                >
                  ({parts.length})
                </span>
              </h3>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={downloadTemplate}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-gray-300 hover:border-[#1F3864] hover:bg-[#F4F6F8] text-[#1F3864] rounded-sm text-xs font-medium"
                  data-testid="new-pcp-template-btn"
                >
                  <Download className="w-3.5 h-3.5" />
                  Descarregar template Excel
                </button>
                <button
                  type="button"
                  onClick={onPickFile}
                  disabled={importBusy}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-[#1F3864] hover:bg-[#142542] disabled:bg-[#9CA3AF] disabled:cursor-not-allowed text-white rounded-sm text-xs font-medium"
                  data-testid="new-pcp-import-btn"
                >
                  <Upload className="w-3.5 h-3.5" />
                  {importBusy ? "A processar…" : "Importar peças do Excel"}
                </button>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".xlsx,.xls"
                  className="hidden"
                  onChange={onFileSelected}
                  data-testid="new-pcp-file-input"
                />
              </div>
            </div>

            {importError && (
              <div className="mb-2 px-3 py-2 bg-[#F8D7DA] text-[#721C24] border border-[#DC3545]/30 rounded-sm text-xs flex items-start gap-2" data-testid="new-pcp-import-error">
                <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>{importError}</span>
              </div>
            )}

            <div
              className="bg-white border border-gray-200 rounded-sm overflow-hidden"
              data-testid="new-pcp-parts-table"
            >
              <table className="w-full text-sm">
                <thead className="bg-[#F4F6F8] text-[10px] uppercase tracking-wider text-[#6C757D]">
                  <tr>
                    <th className="px-3 py-2 text-left">Nº da peça</th>
                    <th className="px-3 py-2 text-left">Descrição</th>
                    <th className="px-3 py-2 text-right">Qtd</th>
                    <th className="px-3 py-2 text-left">Marca</th>
                    <th className="px-3 py-2 text-left">Fonte</th>
                    <th className="px-3 py-2 text-left">Notas</th>
                    <th className="px-3 py-2 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {parts.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-3 py-4 text-center text-xs text-[#6C757D]">
                        Sem peças ainda. Descarrega o template Excel, preenche e importa.
                      </td>
                    </tr>
                  ) : (
                    parts.map((p, i) => (
                      <tr key={i} className="border-t border-gray-100" data-testid={`new-pcp-part-row-${i}`}>
                        <td className="px-3 py-2 font-mono text-xs text-[#1F3864]">{p.part_number}</td>
                        <td className="px-3 py-2 text-xs">{p.description}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs">{p.quantity}</td>
                        <td className="px-3 py-2 text-xs">{p.brand}</td>
                        <td className="px-3 py-2 text-xs">{p.source}</td>
                        <td className="px-3 py-2 text-xs text-[#6C757D]">{p.notes || "—"}</td>
                        <td className="px-3 py-2">
                          <button
                            type="button"
                            onClick={() => removePart(i)}
                            className="text-[#DC3545] hover:text-[#a82733]"
                            aria-label="Remover linha"
                            data-testid={`new-pcp-part-remove-${i}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {err && (
            <div
              className="px-3 py-2 bg-[#F8D7DA] text-[#721C24] border border-[#DC3545]/30 rounded-sm text-sm"
              data-testid="new-pcp-error"
            >
              {err}
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-white px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border border-gray-300 rounded-sm hover:bg-[#F4F6F8]"
            data-testid="new-pcp-cancel"
          >
            Cancelar
          </button>
          <button
            onClick={submit}
            disabled={busy || !canSubmit}
            className="px-4 py-2 text-sm bg-[#1F3864] hover:bg-[#142542] text-white rounded-sm disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="new-pcp-submit"
          >
            {busy ? "A criar…" : parts.length ? `Guardar PCP (${parts.length} peça${parts.length === 1 ? "" : "s"})` : "Guardar PCP"}
          </button>
        </div>
      </div>

      {validationErrors && (
        <ValidationErrorsDialog
          data={validationErrors}
          onClose={() => setValidationErrors(null)}
        />
      )}
      {preview && (
        <PreviewDialog
          data={preview}
          onCancel={() => setPreview(null)}
          onConfirm={confirmImport}
        />
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">{label}</span>
      {children}
    </label>
  );
}

function ReadRow({ label, value, testid, mono }) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">{label}</span>
      <span
        className={`text-[13px] text-[#1A1A2E] ${mono ? "font-mono" : ""}`}
        data-testid={testid}
      >
        {value || "—"}
      </span>
    </div>
  );
}

function ValidationErrorsDialog({ data, onClose }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-md shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="new-pcp-import-errors"
      >
        <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between bg-[#F8D7DA]">
          <h3 className="font-heading text-base font-bold text-[#721C24]">
            {data.errors.length} erro{data.errors.length === 1 ? "" : "s"} encontrado{data.errors.length === 1 ? "" : "s"}
          </h3>
          <button onClick={onClose} className="text-[#721C24] hover:text-[#1A1A2E]">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F4F6F8] text-[10px] uppercase tracking-wider text-[#6C757D] sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left w-16">Linha</th>
                <th className="px-3 py-2 text-left">Coluna</th>
                <th className="px-3 py-2 text-left">Valor</th>
                <th className="px-3 py-2 text-left">Esperado</th>
              </tr>
            </thead>
            <tbody>
              {data.errors.map((e, i) => (
                <tr key={i} className="border-t border-gray-100">
                  <td className="px-3 py-2 font-mono text-xs">{e.row}</td>
                  <td className="px-3 py-2 text-xs text-[#1A1A2E]">{e.column}</td>
                  <td className="px-3 py-2 text-xs text-[#DC3545] font-mono break-all">{e.value}</td>
                  <td className="px-3 py-2 text-xs text-[#6C757D]">{e.expected}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-5 py-3 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm bg-[#1F3864] hover:bg-[#142542] text-white rounded-sm"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}

function PreviewDialog({ data, onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={onCancel}>
      <div
        className="bg-white rounded-md shadow-xl w-full max-w-3xl max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="new-pcp-import-preview"
      >
        <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#1F3864] flex items-center gap-1">
              <FileSpreadsheet className="w-3 h-3" /> Pré-visualização
            </div>
            <h3 className="font-heading text-base font-bold text-[#1A1A2E]">
              {data.total} linha{data.total === 1 ? "" : "s"} prontas para adicionar
            </h3>
          </div>
          <button onClick={onCancel} className="text-[#6C757D] hover:text-[#1A1A2E]">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F4F6F8] text-[10px] uppercase tracking-wider text-[#6C757D] sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left">Nº da peça</th>
                <th className="px-3 py-2 text-left">Descrição</th>
                <th className="px-3 py-2 text-right">Qtd</th>
                <th className="px-3 py-2 text-left">Marca</th>
                <th className="px-3 py-2 text-left">Fonte</th>
                <th className="px-3 py-2 text-left">Notas</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r, i) => (
                <tr key={i} className="border-t border-gray-100">
                  <td className="px-3 py-2 font-mono text-xs text-[#1F3864]">{r.part_number}</td>
                  <td className="px-3 py-2 text-xs">{r.description}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs">{r.quantity}</td>
                  <td className="px-3 py-2 text-xs">{r.brand}</td>
                  <td className="px-3 py-2 text-xs">{r.source}</td>
                  <td className="px-3 py-2 text-xs text-[#6C757D]">{r.notes || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm border border-gray-300 rounded-sm hover:bg-[#F4F6F8]"
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 text-sm bg-[#1F3864] hover:bg-[#142542] text-white rounded-sm flex items-center gap-1.5"
            data-testid="new-pcp-import-confirm"
          >
            <CheckCircle2 className="w-3.5 h-3.5" />
            Adicionar {data.total} linha{data.total === 1 ? "" : "s"}
          </button>
        </div>
      </div>
    </div>
  );
}

const inputCls =
  "w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3864]/30";
